import usePagination from "./usePagination";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/solid";
import { classNames } from "../../helpers";
import { useTranslation } from "react-i18next";

type PaginationProps = {
  currentPage: number;
  numberOfPages: number;
  hasNext: boolean;
  hasPrev: boolean;
  goToPage: (pageNumber: number) => void;
};
const Pagination = (props: PaginationProps) => {
  const { currentPage, hasPrev, hasNext, numberOfPages, goToPage } = props;
  const LocalBasePath = "pagination";
  const { t } = useTranslation("", { keyPrefix: LocalBasePath });
  const pagination = usePagination({
    numberOfPages: numberOfPages,
    initialPage: currentPage,
    maxButtons: 3,
    hasNext,
    hasPrev,
  });
  const visiblePiecesCount = pagination.visiblePieces.length;
  function PagesGenerator() {
    return pagination.visiblePieces.map((page, index) => {
      if (page.type === "page-number") {
        return (
          <button
            key={`page-explore-${page.pageNumber}`}
            onClick={() => {
              goToPage(page.pageNumber);
            }}
            data-active={pagination.activePage === page.pageNumber}
            data-cy={`page${page.pageNumber}`}
            aria-current="page"
            className={classNames(
              pagination.activePage === page.pageNumber &&
              " text-brandblue-600 border-brandblue-500 z-10 bg-brandblue-50",
              `bg-white border-gray-300 text-gray-500   hover:bg-brandblue-600 hover:border-brandblue-500 hover:text-gray-50 relative inline-flex items-center px-4 py-2 border text-sm font-medium  `
            )}
          >
            {page.pageNumber}
          </button>
        );
      } else if (page.type === "ellipsis") {
        return (
          <div
            key={`page-explore..-${index}`}
            aria-current="page"
            className="relative  inline-flex items-center  px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500"
          >
            {"..."}
          </div>
        );
      }
    });
  }
  return (
    <>
      {!!numberOfPages && numberOfPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => {
                goToPage(pagination.visiblePieces[0].pageNumber);
              }}
              className={classNames(
                "relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50",
                !hasPrev && "cursor-not-allowed opacity-50"
              )}
            >
              {t("previous")}
            </button>
            <button
              onClick={() => {
                goToPage(
                  pagination.visiblePieces[visiblePiecesCount - 1].pageNumber
                );
              }}
              className={classNames(
                "ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50",
                !hasNext && "cursor-not-allowed opacity-50"
              )}
            >
              {t("next")}
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                {t("pages", {
                  currentPage: currentPage,
                  totalPages: numberOfPages,
                })}
              </p>
            </div>
            <nav
              className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
              aria-label="Pagination"
            >
              <button
                data-cy="prevButton"
                onClick={() => {
                  goToPage(pagination.visiblePieces[0].pageNumber);
                }}
                className={classNames(
                  "relative rtl:rotate-180 inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50",
                  !hasPrev && "cursor-not-allowed opacity-50"
                )}
              >
                <span className="sr-only">Previous</span>
                <ChevronLeftIcon className="h-5 w-5 " aria-hidden="true" />
              </button>
              <div className="hidden sm:block" data-cy="paginationPages">{PagesGenerator()}</div>
              <button
                data-cy="nextButton"
                onClick={() => {
                  goToPage(
                    pagination.visiblePieces[visiblePiecesCount - 1].pageNumber
                  );
                }}
                className={classNames(
                  "relative rtl:rotate-180 inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50",
                  !hasNext && "cursor-not-allowed opacity-50"
                )}
              >
                <span className="sr-only ">Next</span>
                <ChevronRightIcon className="h-5 w-5  " aria-hidden="true" />
              </button>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default Pagination;
